import {
  Component,
  HostListener,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { NgProgress } from '@ngx-progressbar/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import {
  FileRestrictions,
  FileState,
  SelectEvent,
  UploadComponent
} from '@progress/kendo-angular-upload';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ExportToExcelService } from '../../../../shared/services/export-to-excel.service';
import {
  UIActiveDialogService,
  UIDialogService
} from '../../../../shared/services/ui-dialog.service';
import { BulkAddProgressDialog } from '../../../bulkAdd/shared/bulk-add-progress-dialog/bulk-add-progress-dialog.component';
import { PleaseCheckData } from '../../../bulkAdd/shared/please-check-grid-dialog/please-check-grid-dialogue.component';
import { AuthService } from '../../../security/service/auth.service';
import { BulkAddAssetStatusService } from '../../service/bulk-add-asset-status.service';
import { AssetStatusUpsertArrayModel } from '../model/bulk-add-asset-status-array.model';
import { AssetStatusUpsertModel } from '../model/bulk-add-asset-status.model';
import { BulkAddUIMessages } from '../model/bulk-add-ui.messages';

@Component({
  selector: 'bulk-add-assetStatus',
  encapsulation: ViewEncapsulation.None,
  templateUrl:
    'console/app-js/modules/asset/components/bulk-add-asset-status/bulk-add-asset-status.component.html'
})
export class BulkAddAssetsStatusComponent extends APIOperableComponent {
  protected data: any = [];
  protected numAssets: number;
  protected errorCountSubmit = 0;
  protected indexOfRecordsSent = 1;
  protected failedRows = '';
  protected errorArr: any = [];
  private errorArrFront: any = [];
  protected gridView: GridDataResult;
  protected pageSize = 10;
  protected skip = 0;
  public fileState = FileState;
  private assetObservables: Observable<any>[] = [];

  private BATCH_REQUEST_NUMBER = 50;
  private EXCEL_FILE_NAME = 'BulkAddAssetError.xlsx';

  // block all other file types other than .xlsx files
  public uploadRestrictions: FileRestrictions = {
    allowedExtensions: ['.xlsx']
  };
  protected templateText = 'Status Upload Template';

  private updatedItems: any = [];
  private createdItems: any = [];
  private recordsSent = 0;
  private errorTrys = 0;

  @ViewChild('fileUpload', { static: true }) fileUpload: UploadComponent;

  constructor(
    private exportToExcelService: ExportToExcelService,
    private bulkAddAssetStatusService: BulkAddAssetStatusService,
    private dialogOpenService: UIDialogService,
    private dialogCloseService: UIActiveDialogService,
    private sanitizer: DomSanitizer,
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    super(progressService, toastr, authService);
  }

  @HostListener('window:beforeunload')
  canDeactivate() {
    return this.ableToNavigateAway();
  }

  ableToNavigateAway(): boolean {
    if (this.updatedItems.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  // Kendo UI Upload select event, when a file is selected for upload
  public selectEventHandler(event: SelectEvent): void {
    // testing
    const self = this;
    self.resetErrorGrid();

    event.files.forEach((file: any) => {
      if (!file.validationErrors) {
        const reader: FileReader = new FileReader();

        reader.onload = function(e: any) {
          /* read workbook */
          /* with base64 have to replace the beginning part of the string that tells what format it is in in order for it to decode properly */
          const bstr: string = e.target.result.replace(
            /^data:(.*?);base64,/,
            ''
          );
          const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'base64' });

          /* grab first sheet */
          const wsname: string = wb.SheetNames[0];
          const ws: XLSX.WorkSheet = wb.Sheets[wsname];

          /* save data */
          self.data = XLSX.utils.sheet_to_json(ws, { blankrows: false });
          self.bulkAddAssetValidation(self.data);
        };

        reader.readAsDataURL(file.rawFile);
      } else {
        console.error('Error encountered in selectEventHandler.');
      }
    });
  }

  // Reset grid between uploads so that the grid of errors is only for the current upload
  private resetErrorGrid() {
    const self = this;

    self.gridView = {
      data: null,
      total: 0
    };

    // reset the grid page to zero
    self.skip = 0;

    self.errorArr = [];
    self.errorArrFront = [];
    self.numAssets = 0;
    self.failedRows = '';
  }

  private rowValidation(asset: any): any {
    let colArr = [];

    if (asset.ApiId === undefined || asset.ApiId === '') {
      asset.Issues =
        asset.Issues === undefined
          ? BulkAddUIMessages.ApiId
          : asset.Issues + '; ' + BulkAddUIMessages.ApiId;
      colArr.push('apiId');
    } else if (asset.ApiId !== undefined && isNaN(asset.ApiId)) {
      asset.Issues =
        asset.Issues === undefined
          ? BulkAddUIMessages.ApiIdInvalid
          : asset.Issues + '; ' + BulkAddUIMessages.ApiIdInvalid;
      colArr.push('apiId');
    }

    if (asset.Status === undefined) {
      asset.Issues =
        asset.Issues === undefined
          ? BulkAddUIMessages.Status
          : asset.Issues + '; ' + BulkAddUIMessages.Status;
      colArr.push('status');
    }

    if (colArr.length > 0) {
      asset.Column = colArr;
      this.errorArrFront.push(asset);
      return true;
    } else {
      delete asset.Issues;
      delete asset.Column;
      return asset;
    }
  }

  private formatBadErrors(res) {
    let badAssetArray = [];
    let loadedRecords = 0;
    for (let obs of res) {
      loadedRecords += obs.loadedRecords;
      badAssetArray = badAssetArray.concat(obs.badAssetsList);
    }
    badAssetArray.forEach(badAsset => {
      Object.keys(badAsset.value)
        .map(key => badAsset.value[key])
        .forEach(val => {
          if (badAsset.key.Issues === undefined) {
            badAsset.key.Issues = val;
          } else {
            badAsset.key.Issues = badAsset.key.Issues + ';' + val;
          }
        });
      badAsset.key.Column = Object.keys(badAsset.value);
      this.errorArr.push(this.mapToUpsertAssetModelBE(badAsset.key));
    });
    this.numAssets = loadedRecords;
    if (this.numAssets === this.recordsSent) {
      this.onAPISuccess(
        `${this.recordsSent} Assets Status Uploaded Succesfully`
      );
    } else if (this.errorArr.length > 0) {
      this.onAPIError(
        `${this.errorArr.length + this.errorArrFront.length} Assets with errors`
      );
    }
    this.dialogCloseService.close(BulkAddProgressDialog);
    this.errorArr = this.errorArr.concat(this.errorArrFront);
    this.loadItems();
  }

  private bulkAddAssetValidation(assets: AssetStatusUpsertModel[]) {
    // let serialNumberDupObject = {};
    this.recordsSent = assets.length;
    let upsertAssets = new AssetStatusUpsertArrayModel();
    upsertAssets.upsertArray = [];
    let assetUpsertArrayModelArr: AssetStatusUpsertArrayModel[] = [];
    this.dialogOpenService.open(BulkAddProgressDialog, []);
    for (let x = 1; x <= assets.length; x++) {
      let asset = this.mapToUpsertAssetModelCS(assets[x - 1]);
      asset.Issues = undefined;
      let assetUpload = this.rowValidation(asset);
      if (assetUpload !== true) {
        upsertAssets.upsertArray.push(asset);
      }
      if (x % this.BATCH_REQUEST_NUMBER === 0 || assets.length - x === 0) {
        assetUpsertArrayModelArr.push(Object.assign({}, upsertAssets));
        upsertAssets.upsertArray = [];
      }
    }
    this.concurrentUpload(assetUpsertArrayModelArr);
  }

  private mapToUpsertAssetModelBE(badAsset: any): AssetStatusUpsertModel {
    let badAssetUpsert = new AssetStatusUpsertModel();
    badAssetUpsert.ApiId = badAsset.apiId;
    badAssetUpsert.Status = badAsset.status;
    badAssetUpsert.Issues = badAsset.Issues;
    badAssetUpsert.Column = badAsset.Column;
    return badAssetUpsert;
  }

  private mapToUpsertAssetModelCS(asset: any): AssetStatusUpsertModel {
    asset = this.trimObj(asset);
    let assetUpsert = new AssetStatusUpsertModel();
    assetUpsert.ApiId = asset.ApiId;
    assetUpsert.Status = asset.Status;
    assetUpsert.Issues = asset.Issues;
    assetUpsert.Column = [];
    return assetUpsert;
  }
  private updateSessionDuringBulkUpload() {
    let sessionGauge = 60 * 60 * 1500;
    let expiresTime = localStorage.expires_at;
    let currentTime = Date.now();
    if (expiresTime - currentTime <= sessionGauge) {
      this.authService.getConfigAndInitAuth0().subscribe(() => {
        this.authService.renewToken();
      });
    }
  }

  private trimObj(o: any) {
    return JSON.parse(JSON.stringify(o).replace(/"\s+|\s+"/g, '"'));
  }

  private addAPITimeoutErrorMessage(assets) {
    for (let i = 0; i < assets.upsertArray.length; i++) {
      assets.upsertArray[i].Issues = 'API error, Please resubmit record';
      assets.upsertArray[i].Column = ['apiId'];

      this.errorArrFront.push(
        this.mapToUpsertAssetModelCS(assets.upsertArray[i])
      );
    }
  }

  private concurrentUpload(upsertAssetsArray: AssetStatusUpsertArrayModel[]) {
    this.bulkAddAssetStatusService
      .assetStatusUpsert(upsertAssetsArray[0])
      .subscribe(
        (res: any) => {
          // add results to assetObservables
          this.assetObservables.push(res);
        },
        err => {
          this.updateSessionDuringBulkUpload();
          if (upsertAssetsArray.length === 0) {
            this.dialogCloseService.close(BulkAddProgressDialog);
            this.formatBadErrors(this.assetObservables);
            this.assetObservables = [];
            this.onAPIError('Error Uploading Assets');
          } else {
            if (this.errorTrys < 2) {
              this.errorTrys++;
              this.concurrentUpload(upsertAssetsArray);
            } else {
              this.errorTrys = 0;
              this.addAPITimeoutErrorMessage(upsertAssetsArray[0]);
              upsertAssetsArray = upsertAssetsArray.slice(1);
              this.concurrentUpload(upsertAssetsArray);
            }
          }
        },
        () => {
          this.updateSessionDuringBulkUpload();
          this.errorTrys = 0;
          upsertAssetsArray = upsertAssetsArray.slice(1);
          if (upsertAssetsArray.length === 0) {
            this.formatBadErrors(this.assetObservables);
            this.assetObservables = [];
          } else {
            this.concurrentUpload(upsertAssetsArray);
          }
        }
      );
  }

  protected pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.loadItems();
  }

  private loadItems(): void {
    this.gridView = {
      data: this.errorArr.slice(this.skip, this.skip + this.pageSize),
      total: this.errorArr.length
    };
  }

  private colorCode(record: string[], index: number, dataFromRow): SafeStyle {
    let result: string;
    for (let col of record) {
      if (this.rightIndex(col, index)) {
        if (
          dataFromRow.ColumnsEdited !== undefined &&
          dataFromRow.ColumnsEdited.includes(col)
        ) {
          result = '#98FB98';
        } else {
          result = '#f4f776';
        }
      }
    }
    return this.sanitizer.bypassSecurityTrustStyle(result);
  }

  private rightIndex(columnName: string, index: number): boolean {
    const COL_INDICES = {
      apiId: 0,
      status: 1
    };
    if (COL_INDICES[columnName] === index) {
      return true;
    } else {
      return false;
    }
  }

  // Start of in cell editing functions
  public cellClickHandler({
    sender,
    rowIndex,
    column,
    columnIndex,
    dataItem,
    isEdited
  }) {
    if (!isEdited && this.isWriteOnly(column.field, dataItem.Column)) {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
    }
  }

  public cellCloseHandler(args: any) {
    const { formGroup, dataItem } = args;

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (dataItem.ColumnsEdited === undefined) {
        dataItem.ColumnsEdited = [];
      }
      if (
        !dataItem.ColumnsEdited.includes(
          args.column.field.charAt(0).toLowerCase() + args.column.field.slice(1)
        )
      ) {
        dataItem.ColumnsEdited.push(
          args.column.field.charAt(0).toLowerCase() + args.column.field.slice(1)
        );
      }
      this.assignValues(dataItem, formGroup.value);
      this.update(dataItem, args.rowIndex);
    }
  }

  public hasChanges(): boolean {
    return Boolean(this.updatedItems.length || this.createdItems.length);
  }

  public assignValues(target: any, source: any): void {
    Object.assign(target, source);
  }

  public update(item: any, index): void {
    let x = this.updatedItems.length;
    let modifiedExistingRow = false;
    item.rowIndex = index;
    // check if row has already been modified then replace existing object in updated items array
    for (let i = 0; i < x; i++) {
      if (index === this.updatedItems[i].rowIndex) {
        this.updatedItems[i] = item;
        modifiedExistingRow = true;
        break;
      }
    }
    if (modifiedExistingRow === false) {
      this.updatedItems.push(item);
    }
  }

  public saveHandler({ sender, formGroup, rowIndex }) {
    if (formGroup.valid) {
      sender.closeRow(rowIndex);
    }
  }

  public saveChanges(grid: any): void {
    grid.closeCell();
    grid.cancelCell();
    let rowsCanBeSubmitted = true;
    // add logic to submit data
    for (let t = 0; t < this.updatedItems.length; t++) {
      if (
        !(
          this.updatedItems[t].Column.length ===
          this.updatedItems[t].ColumnsEdited.length
        )
      ) {
        rowsCanBeSubmitted = false;
        break;
      }
    }
    if (rowsCanBeSubmitted) {
      for (let j = 0; j < this.updatedItems.length; j++) {
        for (let i = 0; i < this.errorArr.length; i++) {
          if (
            this.errorArr[i].hasOwnProperty('rowIndex') &&
            this.errorArr[i].rowIndex === this.updatedItems[j].rowIndex
          ) {
            this.errorArr.splice(i, 1);
            break; // so that it doesn't keep looping, after finding a match
          }
        }
      }
      let t0 = Date.now();
      this.errorArrFront = [];
      this.bulkAddAssetValidation(this.updatedItems);
      this.updatedItems = [];
    } else {
      this.dialogOpenService.open(PleaseCheckData, []);
    }
  }

  public createFormGroup(dataItem: any): FormGroup {
    return this.formBuilder.group({
      ApiId: dataItem.ApiId,
      Status: dataItem.Status
    });
  }

  private isWriteOnly(field: string, columns: any): boolean {
    let writeOnlyColumns = columns.slice();
    for (let i = 0; i < writeOnlyColumns.length; i++) {
      writeOnlyColumns[i] = writeOnlyColumns[i].toUpperCase();
    }
    let fieldStuff = field.toUpperCase();
    let test = writeOnlyColumns.indexOf(fieldStuff);
    return test >= 0;
  }
  // end of in cell editing functions

  public onExportResults(): void {
    let jsonData = this.errorArr.map((row: any) => {
      return {
        ApiId: row.ApiId,
        Status: row.Status,
        Issues: row.Issues
      };
    });
    this.exportToExcelService.exportFromJsonToExcel(
      jsonData,
      this.EXCEL_FILE_NAME,
      'xlsx'
    );
  }
}
